import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import { type Product } from "../../shared/products.ts";
import { useProducts } from "./products-context.tsx";

export type CartLine = {
  slug: string;
  quantity: number;
  creationSlug?: string;
  customName?: string;
};

type CartState = {
  lines: CartLine[];
};

type CartAction =
  | { type: "add"; slug: string; quantity: number; creationSlug?: string; customName?: string }
  | { type: "set"; slug: string; quantity: number }
  | { type: "remove"; slug: string }
  | { type: "clear" }
  | { type: "hydrate"; lines: CartLine[] };

const STORAGE_KEY = "beadore:cart:v1";

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "hydrate":
      return { lines: action.lines };
    case "add": {
      const isCreation = !!action.creationSlug;
      const existing = state.lines.find((l) =>
        isCreation
          ? l.creationSlug === action.creationSlug
          : l.slug === action.slug && !l.creationSlug,
      );
      if (existing) {
        return {
          lines: state.lines.map((l) =>
            l === existing
              ? { ...l, quantity: Math.min(99, l.quantity + action.quantity) }
              : l,
          ),
        };
      }
      return {
        lines: [
          ...state.lines,
          {
            slug: action.slug,
            quantity: Math.min(99, action.quantity),
            ...(action.creationSlug
              ? {
                  creationSlug: action.creationSlug,
                  customName: action.customName,
                }
              : {}),
          },
        ],
      };
    }
    case "set":
      return {
        lines: state.lines
          .map((l) =>
            l.slug === action.slug
              ? { ...l, quantity: Math.max(0, Math.min(99, action.quantity)) }
              : l,
          )
          .filter((l) => l.quantity > 0),
      };
    case "remove":
      return { lines: state.lines.filter((l) => l.slug !== action.slug) };
    case "clear":
      return { lines: [] };
  }
}

type CartContextValue = {
  lines: CartLine[];
  itemCount: number;
  subtotalCents: number;
  currency: string;
  resolved: Array<{ line: CartLine; product: Product }>;
  add: (slug: string, quantity?: number, opts?: { creationSlug?: string; customName?: string }) => void;
  setQuantity: (slug: string, quantity: number) => void;
  remove: (slug: string) => void;
  clear: () => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { lines: [] });
  const [hydrated, setHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { products: catalog, live } = useProducts();

  // Load from localStorage once on mount. Slug validity is NOT checked here —
  // the live catalog may not have loaded yet, and dropping a valid line
  // because the API was slow would silently empty carts. Cleanup below runs
  // once the live catalog is known.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { lines?: CartLine[] };
        const clean = (parsed.lines ?? []).filter(
          (l) =>
            typeof l?.slug === "string" &&
            typeof l?.quantity === "number" &&
            l.quantity > 0,
        );
        dispatch({ type: "hydrate", lines: clean });
      }
    } catch {
      // ignore
    }
    setHydrated(true);
  }, []);

  // Once the live catalog is loaded, drop lines for products that no longer
  // exist (removed via the studio bot). Custom-creation lines are kept.
  useEffect(() => {
    if (!live || !hydrated) return;
    const stale = state.lines.filter(
      (l) => !l.creationSlug && !catalog.some((p) => p.slug === l.slug),
    );
    if (stale.length > 0) {
      dispatch({
        type: "hydrate",
        lines: state.lines.filter((l) => !stale.includes(l)),
      });
    }
  }, [live, hydrated, catalog, state.lines]);

  // Persist to localStorage
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ lines: state.lines }));
    } catch {
      // ignore
    }
  }, [state.lines, hydrated]);

  const CUSTOM_CANDLE_PRICE = 4500;
  const CUSTOM_CANDLE_CURRENCY = "usd";

  const resolved = useMemo(
    () =>
      state.lines
        .map((line) => {
          if (line.creationSlug) {
            const pseudo: Product = {
              id: "creation",
              slug: `creation:${line.creationSlug}`,
              name: line.customName ?? "Custom Horoscope Candle",
              subtitle: "Composed for you",
              type: "jar",
              scent: "Your personal composition",
              description: "",
              priceLabel: "$45",
              priceCents: CUSTOM_CANDLE_PRICE,
              currency: CUSTOM_CANDLE_CURRENCY,
              tags: ["Signature"],
              categories: [],
              image: "/products/tuscan-twilight.jpg",
            };
            return { line, product: pseudo };
          }
          const product = catalog.find((p) => p.slug === line.slug);
          return product ? { line, product } : null;
        })
        .filter(
          (r): r is { line: CartLine; product: Product } => r !== null,
        ),
    [state.lines, catalog],
  );

  const itemCount = useMemo(
    () => state.lines.reduce((sum, l) => sum + l.quantity, 0),
    [state.lines],
  );

  const subtotalCents = useMemo(
    () =>
      resolved.reduce(
        (sum, { line, product }) =>
          sum + (product.priceCents ?? 0) * line.quantity,
        0,
      ),
    [resolved],
  );

  const currency = resolved[0]?.product.currency ?? "usd";

  const add = useCallback(
    (
      slug: string,
      quantity = 1,
      opts?: { creationSlug?: string; customName?: string },
    ) => {
      dispatch({
        type: "add",
        slug,
        quantity,
        creationSlug: opts?.creationSlug,
        customName: opts?.customName,
      });
      setIsOpen(true);
    },
    [],
  );

  const setQuantity = useCallback((slug: string, quantity: number) => {
    dispatch({ type: "set", slug, quantity });
  }, []);

  const remove = useCallback((slug: string) => {
    dispatch({ type: "remove", slug });
  }, []);

  const clear = useCallback(() => {
    dispatch({ type: "clear" });
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value: CartContextValue = {
    lines: state.lines,
    itemCount,
    subtotalCents,
    currency,
    resolved,
    add,
    setQuantity,
    remove,
    clear,
    isOpen,
    open,
    close,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}

export function formatMoney(amountMinor: number, currency: string): string {
  const upper = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: upper,
    }).format(amountMinor / 100);
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${upper}`;
  }
}
